import React, { useState, useMemo } from 'react';
import { MdOutlineCancel, MdPix, MdCreditCard, MdAttachMoney, MdRestaurant, MdErrorOutline } from "react-icons/md";
import { FaCreditCard, FaBarcode, FaCheckCircle, FaMoneyBillWave } from "react-icons/fa";
import { BsCreditCard2FrontFill } from "react-icons/bs";
import { ajustedDate } from "../utils/DateUtils";
import { getLogin } from '../utils/StringUtils';
import { formatCurrency } from '../utils/CurrencyUtils';

const getMethodVisuals = (label) => {
    const l = label.toUpperCase();
    if (l.includes("FOODZ")) return { icon: MdRestaurant, color: "bg-custom_red", label: "Crédito Foodz" };
    if (l.includes("PIX")) return { icon: MdPix, color: "bg-teal-600", label: "Pix" };
    if (l.includes("DINHEIRO")) return { icon: FaMoneyBillWave, color: "bg-green-600", label: "Dinheiro" };
    if (l.includes("DÉBITO") || l.includes("DEBITO")) return { icon: BsCreditCard2FrontFill, color: "bg-blue-600", label: "Débito" };
    if (l.includes("CRÉDITO") || l.includes("CREDITO")) return { icon: FaCreditCard, color: "bg-indigo-600", label: "Crédito" };
    if (l.includes("VR") || l.includes("VA")) return { icon: FaBarcode, color: "bg-orange-500", label: "VA / VR" };
    return { icon: MdCreditCard, color: "bg-gray-600", label: label };
};

const PaymentMethod = ({ totalAmount, onTransactionChange, clientId, acceptedPayments, showCreditCard = false, asaasEnabled = false }) => {
    const role = sessionStorage.getItem("role");
    const isRestrictedUser = role === "PARENT" || role === "STUDENT";

    const PAYMENT_LABELS = {
        "CREDITO FOODZ": "Crédito Foodz",
        "DINHEIRO": "Dinheiro",
        "PIX": "PIX",
        "VA | VR": "VA | VR",
        "CREDITO": "Cartão Crédito",
        "DEBITO": "Cartão Débito"
    };

    const LABEL_TO_BACKEND = Object.fromEntries(
        Object.entries(PAYMENT_LABELS).map(([backend, label]) => [label, backend])
    );

    const parsedPayments = useMemo(() => {
        if (!acceptedPayments) return [];
        return acceptedPayments
            .split(";")
            .map(item => item.trim().toUpperCase())
            .filter(item => item.length > 0);
    }, [acceptedPayments]);

    const schoolHasPix = parsedPayments.includes("PIX");

    const paymentMethods = isRestrictedUser
        ? [
            "Crédito Foodz",
            ...(asaasEnabled && schoolHasPix ? ["PIX"] : []),
            ...(asaasEnabled && showCreditCard ? ["Cartão Crédito"] : []),
          ]
        : parsedPayments.map(backend => PAYMENT_LABELS[backend] || backend);

    const [paymentSections, setPaymentSections] = useState([{
        paymentMethod: '',
        value: 0,
        change: 0,
        received_value: 0.00,
        input: '',
        client_code: sessionStorage.getItem('clientCode'),
        customer_id: getLogin(clientId),
        transactionDate: ajustedDate(),
        transaction_type: 'Order'
    }]);

    const recalculateSections = (sections) => {
        const total = parseFloat(totalAmount);
        const sumOtherMethods = sections
            .filter(s => s.paymentMethod && !s.paymentMethod.startsWith("DINHEIRO"))
            .reduce((acc, s) => acc + (s.received_value || 0), 0);

        return sections.map(section => {
            const method = section.paymentMethod.split('/')[0];
            let value = section.value;
            let received_value = section.received_value;
            let change = 0;

            if (!method) return section;

            if (method === 'DINHEIRO') {
                value = Math.max(total - sumOtherMethods, 0);
                change = received_value > value ? received_value - value : 0;
            } else {
                value = received_value;
            }
            return { ...section, value, received_value, change };
        });
    };

    const updateTransactionList = (updatedSections) => {
        if (onTransactionChange) onTransactionChange(updatedSections);
    };

    const handleMethodSelect = (index, label) => {
        const backendValue = LABEL_TO_BACKEND[label];
        const updatedSections = [...paymentSections];
        const currentMethod = updatedSections[index].paymentMethod.split('/')[0];

        if (currentMethod === backendValue) {
            updatedSections[index] = {
                ...updatedSections[index],
                paymentMethod: '',
                input: '',
                received_value: 0,
                value: 0,
                change: 0
            };
        } else {
            updatedSections[index] = {
                ...updatedSections[index],
                paymentMethod: backendValue,
                customer_id: getLogin(clientId),
                transactionDate: ajustedDate(),
                input: '',
                received_value: 0
            };

            if (isRestrictedUser) {
                const numericTotal = parseFloat(String(totalAmount)) || 0;
                updatedSections[index].received_value = numericTotal;
                updatedSections[index].input = numericTotal.toFixed(2).replace('.', ',');
            }
        }

        const recalculated = recalculateSections(updatedSections);
        setPaymentSections(recalculated);
        updateTransactionList(recalculated);
    };

    const handleCardBrandSelect = (index, brand) => {
        const updatedSections = [...paymentSections];
        const base = updatedSections[index].paymentMethod.split('/')[0];
        updatedSections[index].paymentMethod = `${base}/${brand}`;
        const recalculated = recalculateSections(updatedSections);
        setPaymentSections(recalculated);
        updateTransactionList(recalculated);
    };

    const handleInputChange = (index, event) => {
        const rawValue = event.target.value;
        const sanitizedValue = rawValue.replace(/[^0-9.,]/g, '').replace(',', '.');
        const numericValue = parseFloat(sanitizedValue);

        const updatedSections = [...paymentSections];
        updatedSections[index].input = rawValue;
        updatedSections[index].received_value = !isNaN(numericValue) ? numericValue : 0;

        const recalculated = recalculateSections(updatedSections);
        setPaymentSections(recalculated);
        updateTransactionList(recalculated);
    };

    const handleSetTotalAmount = (index) => {
        const total = parseFloat(totalAmount);
        if (isNaN(total)) return;

        const updatedSections = [...paymentSections];
        updatedSections[index].received_value = total;
        updatedSections[index].input = total.toFixed(2).replace('.', ',');
        
        const recalculated = recalculateSections(updatedSections);
        setPaymentSections(recalculated);
        updateTransactionList(recalculated);
    };

    const addSection = () => {
        setPaymentSections([...paymentSections, {
            paymentMethod: '',
            value: 0,
            change: 0,
            received_value: 0,
            input: '',
            client_code: sessionStorage.getItem('clientCode'),
            customer_id: getLogin(clientId),
            transactionDate: ajustedDate(),
            transaction_type: 'Order'
        }]);
    };

    const removeSection = (idx) => {
        const updated = paymentSections.filter((_, i) => i !== idx);
        const recalculated = recalculateSections(updated);
        setPaymentSections(recalculated);
        updateTransactionList(recalculated);
    };

    const isCardMethod = (pm) => {
        const base = pm.split('/')[0];
        return base === "CREDITO" || base === "DEBITO";
    };

    return (
        <div className="flex flex-col w-full px-2 md:px-0 pb-10">
            <div className="flex flex-col items-center justify-center mb-6">
                <span className="text-gray-500 text-sm font-semibold uppercase tracking-widest">Total a Pagar</span>
                <span className="text-4xl font-bold text-gray-800">{formatCurrency(totalAmount)}</span>
            </div>

            {paymentSections.map((section, index) => {
                const baseMethod = section.paymentMethod.split('/')[0];
                const selectedBrand = section.paymentMethod.split('/')[1];
                const needsBrand = isCardMethod(section.paymentMethod);
                const brandMissing = needsBrand && !selectedBrand;

                return (
                    <div key={index} className="mb-8 relative animate-in fade-in slide-in-from-bottom-4">
                        {index > 0 && (
                            <div className="flex justify-end mb-2">
                                <button onClick={() => removeSection(index)} className="flex items-center gap-1 text-red-500 text-sm font-bold bg-red-50 px-3 py-1 rounded-full">
                                    <MdOutlineCancel /> Remover Divisão
                                </button>
                            </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {paymentMethods.map((label, i) => {
                                const backendValue = LABEL_TO_BACKEND[label];
                                const isSelected = baseMethod === backendValue;
                                const isDimmed = section.paymentMethod && !isSelected;
                                const { icon: Icon, color, label: visualLabel } = getMethodVisuals(label);

                                return (
                                    <div
                                        key={i}
                                        onClick={() => handleMethodSelect(index, label)}
                                        className={`group relative aspect-[5/3] rounded-2xl overflow-hidden cursor-pointer transition-all duration-300
                                            ${isSelected ? "ring-4 ring-custom_blue shadow-xl scale-[1.02]" : "ring-1 ring-slate-200 opacity-90"}
                                            ${isDimmed ? "opacity-30 grayscale" : ""}`}
                                    >
                                        <div className={`absolute inset-0 ${color}`} />
                                        <Icon className="absolute -right-4 -bottom-4 text-white opacity-10 text-[100px] transform -rotate-12" />
                                        {isSelected && <div className="absolute top-3 right-3 bg-white text-custom_blue rounded-full p-1"><FaCheckCircle size={18} /></div>}
                                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-10 p-2 text-center">
                                            <Icon className="text-4xl mb-2" />
                                            <span className="font-bold text-sm leading-tight">{visualLabel}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {section.paymentMethod && (
                            <div className={`mt-4 bg-gray-50 rounded-xl p-4 border-2 transition-all ${brandMissing ? 'border-red-300 bg-red-50' : 'border-transparent bg-gray-50'}`}>
                                
                                {needsBrand && (
                                    <div className="mb-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide">Selecione a Bandeira</label>
                                            {brandMissing && <span className="text-[10px] text-red-500 font-bold animate-pulse flex items-center gap-0.5"><MdErrorOutline/> Obrigatório</span>}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {["Visa", "Mastercard", "Elo", "Amex", "Outro"].map(brand => (
                                                <button
                                                    key={brand}
                                                    onClick={() => handleCardBrandSelect(index, brand)}
                                                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all
                                                        ${selectedBrand === brand ? 'bg-custom_blue text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200'}`}
                                                >
                                                    {brand}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* BLOQUEIO: Só mostra o input de valor se não faltar a bandeira */}
                                {!brandMissing ? (
                                    <div className="flex flex-col md:flex-row md:items-end gap-4 animate-in fade-in duration-300">
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Valor a Cobrar</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">R$</span>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={section.input || ''}
                                                    onChange={(e) => handleInputChange(index, e)}
                                                    disabled={isRestrictedUser}
                                                    className="w-full pl-10 pr-4 py-3 text-2xl font-bold rounded-lg border-2 border-gray-300 outline-none focus:border-custom_blue text-gray-800"
                                                    placeholder="0,00"
                                                />
                                            </div>
                                        </div>
                                        {!isRestrictedUser && (
                                            <button onClick={() => handleSetTotalAmount(index)} className="h-14 px-6 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg shadow-md flex items-center justify-center">
                                                <MdAttachMoney className="text-xl" /> Cobrar Total
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="py-4 text-center text-red-400 flex flex-col items-center gap-2">
                                        <MdCreditCard size={32} className="opacity-50" />
                                        <p className="text-sm font-semibold">Escolha a bandeira acima para liberar a cobrança.</p>
                                    </div>
                                )}

                                {section.change > 0 && (
                                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between text-yellow-800">
                                        <span className="font-bold text-sm uppercase">Troco:</span>
                                        <span className="font-bold text-xl">{formatCurrency(section.change)}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {!isRestrictedUser && (
                <button onClick={addSection} className="w-full py-3 border-2 border-dashed border-gray-300 text-gray-500 font-bold rounded-xl hover:border-custom_blue hover:bg-blue-50 transition-all flex items-center justify-center gap-2">
                    <MdCreditCard /> Dividir Pagamento
                </button>
            )}
        </div>
    );
};

export default PaymentMethod;